import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGoBack } from "@/hooks/use-go-back";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/features/forms/ui/bundles-multi-select";
import { toast } from "@/hooks/use-toast";
import { ALL_REGIONS_CODE } from "@/features/regions/constants";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import { getProducts } from "@/features/products/products-api";
import { getForms } from "@/features/bundles/services/get-active-form";
import useBundlesGrouped from "@/features/bundles/hooks/use-bundles-grouped";
import {
  ProductRegionConfigInput,
  createProductRegionConfig,
  updateProductRegionConfig,
  uploadProductRegionConfigImage,
  useProductRegionConfig,
  useProductRegionConfigSiblings,
  type PropagationGroup,
} from "@/features/products/product-region-config-api";
import OnboardingRoutingField, {
  RoutingRule,
  RoutingQuestion,
} from "@/features/products/ui/onboarding-routing-field";
import RoundRobinRoutingField, {
  RoundRobinRule,
} from "@/features/products/ui/round-robin-routing-field";
import { getManagedEventsByBundles } from "@/features/supply-config/managed-events-api";
import { getEventTypesByManagedEvent } from "@/features/supply-config/event-types-api";
import ImageUploadField from "@/features/products/ui/image-upload-field";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AssessmentPagesField,
  type AssessmentPagesValue,
} from "@/features/products/ui/assessment-pages-field";
import CardColorField from "@/features/products/ui/card-color-field";
import { getForm } from "@/features/forms/services/get-form";
import SessionNamesField from "@/features/products/ui/session-names-field";
import { useHasAccess } from "@/components/common/has-access";

// Titled bordered card used to group the config into sections (mirrors the
// bundle edit page's SectionCard).
function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-4 min-w-0">
      <div className="space-y-0.5">
        <h3 className="text-lg font-medium">{title}</h3>
        {description && <p className="text-xs text-gray-400">{description}</p>}
      </div>
      {children}
    </div>
  );
}

// Per-section "also apply to the other regions" toggle. Rendered only inside the
// sections whose content carries no region-scoped reference (images, subtitle,
// card, pricing copy) — everything else on this page is region-bound and has no
// toggle at all, so there's no way to tick something that shouldn't cross.
function PropagateToggle({
  checked,
  onToggle,
  regions,
  note,
}: {
  checked: boolean;
  onToggle: () => void;
  // e.g. "AE" / "AE, US" — the regions this would write to.
  regions: string;
  note?: string;
}) {
  return (
    <label className="flex items-start gap-2 rounded-md border border-dashed border-gray-300 bg-gray-50/60 p-2.5 cursor-pointer">
      <Checkbox
        checked={checked}
        onCheckedChange={onToggle}
        className="mt-0.5"
      />
      <span className="text-xs text-gray-600">
        Also apply this section to {regions}
        {note && <span className="block text-gray-400">{note}</span>}
      </span>
    </label>
  );
}

// Pull choice options out of a raw form question (Rocket: options[]; Typeform:
// properties.choices[].label; plus a couple of other shapes seen in the data).
function extractOptions(q: any): string[] {
  if (Array.isArray(q?.options)) return q.options;
  if (Array.isArray(q?.properties?.choices))
    return q.properties.choices
      .map((c: any) => c?.label ?? c?.text ?? "")
      .filter(Boolean);
  if (Array.isArray(q?.choices?.labels)) return q.choices.labels;
  if (Array.isArray(q?.choices))
    return q.choices
      .map((c: any) => (typeof c === "string" ? c : (c?.label ?? c?.text)))
      .filter(Boolean);
  return [];
}

function normalizeQuestions(form: any): RoutingQuestion[] {
  const qs = Array.isArray(form?.questions) ? form.questions : [];
  return qs
    .filter((q: any) => q?.id)
    .map((q: any) => ({
      id: q.id,
      title: q.title ?? q.id,
      options: extractOptions(q),
      type: typeof q.type === "string" ? q.type : undefined,
    }));
}

// Per-form presentation + picker-box styling, keyed by Form id. Kept flat for
// the form; serialized into onboarding_form_meta[id] on save.
type FormMetaEntry = {
  title: string;
  subtitle: string;
  image: string;
  slug: string;
  showOnDashboard: boolean;
  // Whether the patient is expected to have filled this before a session. When
  // true and they have not, the appointments session card shows a nudge.
  // Only bites for forms that are ALSO shown on the dashboard — a routing-only
  // form has no surface for the patient to reach.
  mandatoryForSession: boolean;
  // Form-results page layout: 'full' (score/tier meter) or 'reduced' (no score).
  resultsVariant: "full" | "reduced";
  // Picker-box styling. bgType "" → the default white card; colors are hex.
  bgType: "" | "solid" | "gradient";
  bgColors: string[]; // solid uses [0]; gradient uses all (>=2)
  bgDirection: string; // CSS gradient direction keyword
  fontColor: string; // "" → default text color
};

const EMPTY_FORM_META: FormMetaEntry = {
  title: "",
  subtitle: "",
  image: "",
  slug: "",
  showOnDashboard: true,
  mandatoryForSession: true,
  resultsVariant: "full",
  bgType: "",
  bgColors: [],
  bgDirection: "to bottom",
  fontColor: "",
};

// CSS linear-gradient direction keywords (stored verbatim; the dashboard uses
// them directly on web and maps them to a flat color on native).
const GRADIENT_DIRECTIONS: { value: string; label: string }[] = [
  { value: "to right", label: "Left → Right" },
  { value: "to left", label: "Right → Left" },
  { value: "to bottom", label: "Top → Bottom" },
  { value: "to top", label: "Bottom → Top" },
  { value: "to bottom right", label: "Diagonal ↘" },
  { value: "to bottom left", label: "Diagonal ↙" },
  { value: "to top right", label: "Diagonal ↗" },
  { value: "to top left", label: "Diagonal ↖" },
];

// Small hex color input (native picker + text). Used for the solid background,
// each gradient stop, and the font color. Stores a raw hex string ("" clears).
function ColorInput({
  value,
  onChange,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  ariaLabel?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#ffffff"}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-10 rounded border border-gray-200 p-0.5 cursor-pointer shrink-0"
        aria-label={ariaLabel}
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#RRGGBB"
        className="w-28 h-8 rounded-md border border-gray-200 px-2 text-sm font-mono"
      />
    </div>
  );
}

export default function ProductConfig() {
  const { region = ALL_REGIONS_CODE, productId } = useParams();
  const navigate = useNavigate();
  const goBack = useGoBack("..");
  const qc = useQueryClient();
  // The short names save on their own (PATCH /product/:id), which wants write.
  const canWrite = useHasAccess("write");

  const { data: regions } = useActiveRegions();
  const regionId = regions?.find((r) => r.code === region)?.id;

  const { data: products } = useQuery({ queryKey: ["products"], queryFn: getProducts });
  const product = products?.find((p) => p.id === productId);

  // Forms for this region (high limit so the dropdown isn't capped at the
  // endpoint's default of 10). MultiSelect provides client-side search.
  const { data: formsData } = useQuery({
    queryKey: ["forms", "by-region", region],
    queryFn: () =>
      getForms({
        ...(region && region !== ALL_REGIONS_CODE ? { region } : {}),
        limit: 100,
      }),
  });
  const forms = formsData?.forms ?? [];

  // Onboarding bundles for this product+region (from the grouped catalog).
  const { data: grouped } = useBundlesGrouped({});
  const onboardingBundles = useMemo(() => {
    const group = (grouped?.groups ?? []).find(
      (g) => g.product?.id === productId,
    );
    return (group?.offerings ?? [])
      .map((o) => o.current)
      .filter((b) => b && b.type === "onboarding")
      .map((b) => ({ id: b!.id, name: b!.name }));
  }, [grouped, productId]);

  // All of this product's bundles (any type, incl. non-public) — targets for
  // the assessment pages' diagnostic offer.
  const productBundles = useMemo(() => {
    const group = (grouped?.groups ?? []).find(
      (g) => g.product?.id === productId,
    );
    return (group?.offerings ?? [])
      .map((o) => o.current)
      .filter((b): b is NonNullable<typeof b> => !!b)
      .map((b) => ({ standard_id: b.standard_id, name: b.name }));
  }, [grouped, productId]);

  const { data: config, isLoading } = useProductRegionConfig(productId, regionId);

  // Managed events this product's bundles actually book (product+region scoped),
  // for round-robin routing config. Only round-robin-pool ones are configurable.
  const { data: managedEventsData } = useQuery({
    queryKey: ["managed-events", "by-bundles", productId, regionId],
    queryFn: () =>
      getManagedEventsByBundles({
        product_id: productId!,
        region_id: regionId!,
      }),
    enabled: !!productId && !!regionId,
  });
  const roundRobinManagedEvents = useMemo(
    () => (managedEventsData ?? []).filter((m) => m.round_robin_pool_active),
    [managedEventsData],
  );

  // Round-robin pool event types per managed event (professional_id null + has a
  // team), labeled by their `type` slug — the routing/default targets.
  const poolQueries = useQueries({
    queries: roundRobinManagedEvents.map((m) => ({
      queryKey: ["event-types", "by-managed", m.id],
      queryFn: () => getEventTypesByManagedEvent(m.id),
      enabled: !!m.id,
    })),
  });
  const poolsByManagedEvent = useMemo(() => {
    const map: Record<string, { id: string; label: string }[]> = {};
    roundRobinManagedEvents.forEach((m, i) => {
      const ets = poolQueries[i]?.data ?? [];
      map[m.id] = ets
        .filter((et) => !et.professional_id && (et.teams?.length ?? 0) > 0)
        .map((et) => ({
          id: et.id,
          // "<managed event name> - <type>" (type is the pool's disambiguator).
          label: et.type ? `${m.name} - ${et.type}` : m.name,
        }));
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    poolQueries.map((q) => q.dataUpdatedAt).join(","),
    roundRobinManagedEvents.map((m) => m.id).join(","),
  ]);

  const [formIds, setFormIds] = useState<string[]>([]);
  const [defaultBundleId, setDefaultBundleId] = useState<string>("");
  // Cross-product onboarding bridge (metadata.onboarding_satisfied_by_product_ids):
  // owning a bundle from any of these products skips this product's onboarding.
  const [onboardingSatisfiedBy, setOnboardingSatisfiedBy] = useState<string[]>(
    [],
  );
  // Cross-product therapist continuity (metadata.carry_professional_from_product_id):
  // a new bundle of THIS product inherits the professional from the user's most
  // recent bundle/session of the selected source product (e.g. ADHD → Clinical).
  const [carryProfessionalFrom, setCarryProfessionalFrom] = useState<string>("");
  // Per-form presentation for the multi-form picker, keyed by Form id.
  const [formMeta, setFormMeta] = useState<Record<string, FormMetaEntry>>({});
  // Per-form routing rules, keyed by Form id (serialized into the flat
  // onboarding_routing with each rule form-scoped).
  const [rulesByForm, setRulesByForm] = useState<Record<string, RoutingRule[]>>(
    {},
  );
  // Answer→round-robin routing, keyed by managed_event_id:
  // { [meId]: { default_event_type_id, rules } }.
  const [roundRobinByManagedEvent, setRoundRobinByManagedEvent] = useState<
    Record<
      string,
      { default_event_type_id: string; rules: RoundRobinRule[] }
    >
  >({});
  const [subtitle, setSubtitle] = useState("");
  // Product images (uploaded → CDN url stored here).
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [heroUrl, setHeroUrl] = useState("");
  const [heroRadius, setHeroRadius] = useState(""); // px; blank → default
  const [thumbnailRadius, setThumbnailRadius] = useState(""); // px; blank → default
  // Card presentation (metadata.card).
  const [cardTitle, setCardTitle] = useState("");
  const [cardDescription, setCardDescription] = useState("");
  const [tagsText, setTagsText] = useState(""); // comma/newline separated
  const [variant, setVariant] = useState(""); // "" → dashboard default ('light')
  const [accentColor, setAccentColor] = useState(""); // hex; "" → default indigo (#5B5BD6)
  const [visible, setVisible] = useState(true); // show the card on the dashboard
  // Dashboard card order (metadata.priority) — lower shows first; blank = last.
  const [priority, setPriority] = useState<string>("");
  const [metadataText, setMetadataText] = useState(""); // advanced (excl. subtitle + card + visible)
  // Assessment WhatsApp templates (metadata.assessment_notifications). Blank →
  // ADHD default per event (unless other events are set — see save serialization).
  const [notifExploration, setNotifExploration] = useState("");
  const [notifExplorationSlug, setNotifExplorationSlug] = useState("");
  const [notifReport, setNotifReport] = useState("");
  const [notifReportSlug, setNotifReportSlug] = useState("");
  const [notifClosure, setNotifClosure] = useState("");
  // Assessment journey pages (metadata.results_page / report_page).
  const [assessmentPages, setAssessmentPages] = useState<AssessmentPagesValue>({
    results_page: null,
    report_page: null,
  });
  // Assessment journey toggle (metadata.assessment_journey): show the
  // results/report completion CTAs on the product page when a pack finishes.
  const [assessmentJourney, setAssessmentJourney] = useState(false);
  // Pre-payment form-results page toggle (metadata.form_results_page, ADHD):
  // show the High/Moderate/Low screen after form+OTP, before payment. Off →
  // old flow (result shown on the form's own final screen).
  const [formResultsPage, setFormResultsPage] = useState(false);
  // Hide the pack-summary inner card on the booking-confirm session card
  // (metadata.hide_booking_pack_summary).
  const [hideBookingPackSummary, setHideBookingPackSummary] = useState(false);
  // "Assessments & Tests" home-section card (metadata.assessment_card).
  const [assessmentCard, setAssessmentCard] = useState<Record<
    string,
    any
  > | null>(null);
  // Pre-form onboarding intro screen (metadata.onboarding_intro).
  const [onboardingIntro, setOnboardingIntro] = useState<Record<
    string,
    any
  > | null>(null);
  // Onboarding overview page steps copy (metadata.overview_page).
  const [overviewPage, setOverviewPage] = useState<Record<
    string,
    any
  > | null>(null);
  // Choose-a-Bundle / pricing page copy (metadata.pricing_copy).
  const [pricingCopy, setPricingCopy] = useState<Record<
    string,
    any
  > | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // ── Cross-region propagation ──
  // This product's configs in the OTHER regions. Presentation groups can be
  // mirrored onto them; everything else on this page is region-bound (onboarding
  // forms / default bundle / routing reference region-scoped rows, and
  // visibility + priority are the per-region launch controls), so it never
  // crosses. Each group is its own toggle, default OFF — regions are meant to
  // differ, so a cross-region write is always an explicit choice.
  const { data: siblingConfigs = [] } = useProductRegionConfigSiblings(
    productId,
    regionId,
  );
  const siblingLabel = siblingConfigs
    .map((c) => c.region_rel?.code?.toUpperCase() ?? "—")
    .join(", ");
  const canPropagate = !!config && siblingConfigs.length > 0;
  const [propagateGroups, setPropagateGroups] = useState<
    Record<PropagationGroup, boolean>
  >({
    images: false,
    page_details: false,
    card: false,
    pricing_copy: false,
  });
  const toggleGroup = (g: PropagationGroup) =>
    setPropagateGroups((prev) => ({ ...prev, [g]: !prev[g] }));

  useEffect(() => {
    if (!hydrated && config !== undefined) {
      const fids = config?.onboarding_forms?.map((f) => f.id) ?? [];
      setFormIds(fids);
      setDefaultBundleId(config?.onboarding_default_bundle_id ?? "");
      // Per-form presentation.
      const formMetaRaw =
        (config?.onboarding_form_meta as Record<string, any>) ?? {};
      const nextMeta: Record<string, FormMetaEntry> = {};
      fids.forEach((id) => {
        const m = (formMetaRaw[id] as Record<string, any>) ?? {};
        const style = (m.style as Record<string, any>) ?? {};
        const bg = (style.background as Record<string, any>) ?? {};
        const bgType =
          bg.type === "solid" || bg.type === "gradient" ? bg.type : "";
        nextMeta[id] = {
          title: typeof m.title === "string" ? m.title : "",
          subtitle: typeof m.subtitle === "string" ? m.subtitle : "",
          image: typeof m.image === "string" ? m.image : "",
          slug: typeof m.slug === "string" ? m.slug : "",
          // Default true — a form with no stored flag is shown.
          showOnDashboard: m.show_on_dashboard !== false,
          // Default true — a form with no stored flag is expected before a
          // session, so existing configs become mandatory with no backfill.
          mandatoryForSession: m.mandatory_for_session !== false,
          // Default 'full' — a form with no stored flag shows the scored page.
          resultsVariant: m.results_variant === "reduced" ? "reduced" : "full",
          bgType,
          bgColors: Array.isArray(bg.colors)
            ? bg.colors.filter((c: any) => typeof c === "string")
            : [],
          bgDirection:
            typeof bg.direction === "string" ? bg.direction : "to bottom",
          fontColor: typeof style.fontColor === "string" ? style.fontColor : "",
        };
      });
      setFormMeta(nextMeta);
      // Group the flat routing by form_id (unscoped legacy rules → first form).
      const grouped: Record<string, RoutingRule[]> = {};
      (config?.onboarding_routing ?? []).forEach((r: any) => {
        const fid = r.form_id ?? fids[0];
        if (!fid) return;
        const rule: RoutingRule = {
          form_id: fid,
          match: r.match === "any" ? "any" : "all",
          conditions: (r.conditions ?? []).map((c: any) => ({
            field: c.field ?? "",
            op: c.op ?? "eq",
            value: Array.isArray(c.value)
              ? c.value.join(", ")
              : c.value != null
                ? String(c.value)
                : "",
          })),
          bundle_id: r.bundle_id ?? "",
        };
        (grouped[fid] ??= []).push(rule);
      });
      setRulesByForm(grouped);
      // Round-robin routing (object keyed by managed_event_id).
      const rrRaw =
        (config?.round_robin_routing as Record<string, any>) ?? {};
      const rrNext: Record<
        string,
        { default_event_type_id: string; rules: RoundRobinRule[] }
      > = {};
      Object.entries(rrRaw).forEach(([meId, v]) => {
        const entry = (v as Record<string, any>) ?? {};
        rrNext[meId] = {
          default_event_type_id:
            typeof entry.default_event_type_id === "string"
              ? entry.default_event_type_id
              : "",
          rules: (entry.rules ?? []).map((r: any) => ({
            form_id: r.form_id ?? undefined,
            match: r.match === "any" ? "any" : "all",
            conditions: (r.conditions ?? []).map((c: any) => ({
              field: c.field ?? "",
              op: c.op ?? "eq",
              value: Array.isArray(c.value)
                ? c.value.join(", ")
                : c.value != null
                  ? String(c.value)
                  : "",
            })),
            event_type_id: r.event_type_id ?? "",
          })),
        };
      });
      setRoundRobinByManagedEvent(rrNext);
      const imgs = (config?.images as Record<string, any>) ?? {};
      setThumbnailUrl(typeof imgs.thumbnail === "string" ? imgs.thumbnail : "");
      setHeroUrl(typeof imgs.hero === "string" ? imgs.hero : "");
      setHeroRadius(
        typeof imgs.hero_radius === "number" ? String(imgs.hero_radius) : "",
      );
      setThumbnailRadius(
        typeof imgs.thumbnail_radius === "number"
          ? String(imgs.thumbnail_radius)
          : "",
      );
      // Subtitle + card have their own fields; keep them out of the raw box.
      const meta = (config?.metadata as Record<string, any>) ?? {};
      setSubtitle(typeof meta.subtitle === "string" ? meta.subtitle : "");
      const card = (meta.card as Record<string, any>) ?? {};
      setCardTitle(typeof card.title === "string" ? card.title : "");
      setCardDescription(
        typeof card.description === "string" ? card.description : "",
      );
      setTagsText(Array.isArray(card.tags) ? card.tags.join(", ") : "");
      setVariant(typeof card.variant === "string" ? card.variant : "");
      setAccentColor(
        typeof card.accent_color === "string" ? card.accent_color : "",
      );
      setVisible(meta.visible !== false); // default visible
      setAssessmentPages({
        results_page:
          meta.results_page && typeof meta.results_page === "object"
            ? meta.results_page
            : null,
        report_page:
          meta.report_page && typeof meta.report_page === "object"
            ? meta.report_page
            : null,
      });
      setOnboardingSatisfiedBy(
        Array.isArray(meta.onboarding_satisfied_by_product_ids)
          ? meta.onboarding_satisfied_by_product_ids
          : [],
      );
      setCarryProfessionalFrom(
        typeof meta.carry_professional_from_product_id === "string"
          ? meta.carry_professional_from_product_id
          : "",
      );
      setAssessmentJourney(meta.assessment_journey === true);
      setFormResultsPage(!!meta.form_results_page);
      setHideBookingPackSummary(meta.hide_booking_pack_summary === true);
      setAssessmentCard(
        meta.assessment_card && typeof meta.assessment_card === "object"
          ? meta.assessment_card
          : null,
      );
      setOnboardingIntro(
        meta.onboarding_intro && typeof meta.onboarding_intro === "object"
          ? meta.onboarding_intro
          : null,
      );
      setOverviewPage(
        meta.overview_page && typeof meta.overview_page === "object"
          ? meta.overview_page
          : null,
      );
      setPricingCopy(
        meta.pricing_copy && typeof meta.pricing_copy === "object"
          ? meta.pricing_copy
          : null,
      );
      setPriority(
        typeof meta.priority === "number" ? String(meta.priority) : "",
      );
      const an = (meta.assessment_notifications ?? {}) as Record<string, any>;
      // Each event is a template string OR { template, slug_param }.
      const evtTpl = (v: any): string =>
        typeof v === "string"
          ? v
          : v && typeof v === "object" && typeof v.template === "string"
            ? v.template
            : "";
      const evtSlug = (v: any): string =>
        v && typeof v === "object" && typeof v.slug_param === "string"
          ? v.slug_param
          : "";
      setNotifExploration(evtTpl(an.exploration_outcome));
      setNotifExplorationSlug(evtSlug(an.exploration_outcome));
      setNotifReport(evtTpl(an.diagnostic_report));
      setNotifReportSlug(evtSlug(an.diagnostic_report));
      setNotifClosure(evtTpl(an.journey_closure));
      const {
        subtitle: _s,
        card: _c,
        visible: _v,
        results_page: _rp,
        report_page: _rep,
        assessment_journey: _aj,
        form_results_page: _frp,
        hide_booking_pack_summary: _hbps,
        assessment_card: _ac,
        onboarding_intro: _oi,
        overview_page: _op,
        pricing_copy: _pc,
        onboarding_satisfied_by_product_ids: _osb,
        carry_professional_from_product_id: _cpf,
        priority: _p,
        assessment_notifications: _an,
        ...restMeta
      } = meta;
      setMetadataText(
        Object.keys(restMeta).length ? JSON.stringify(restMeta, null, 2) : "",
      );
      setHydrated(true);
    }
  }, [config, hydrated]);

  // Flatten the per-form routing into the flat onboarding_routing JSON, forcing
  // each rule's form_id to its form. Priority is a running index (only same-form
  // rules ever compete at match time, so cross-form order is cosmetic).
  const buildRouting = () => {
    let priority = 0;
    return formIds.flatMap((fid) =>
      (rulesByForm[fid] ?? [])
        .filter((r) => r.bundle_id)
        .map((r) => ({
          priority: ++priority,
          form_id: fid,
          match: r.match,
          conditions: r.conditions
            .filter((c) => c.field && (c.op === "exists" || c.value !== ""))
            .map((c) => ({
              field: c.field,
              op: c.op,
              ...(c.op === "exists"
                ? {}
                : {
                    value:
                      c.op === "in"
                        ? c.value
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean)
                        : c.value,
                  }),
            })),
          bundle_id: r.bundle_id,
        })),
    );
  };

  // Build the per-form presentation JSON (onboarding_form_meta), keyed by Form id.
  const slugify = (v: string) =>
    v
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  const buildFormMeta = () => {
    const out: Record<string, any> = {};
    formIds.forEach((fid) => {
      const m = formMeta[fid];
      if (!m) return;
      const entry: Record<string, any> = {};
      if (m.title.trim()) entry.title = m.title.trim();
      if (m.subtitle.trim()) entry.subtitle = m.subtitle.trim();
      if (m.image.trim()) entry.image = m.image.trim();
      if (m.slug.trim()) entry.slug = slugify(m.slug);
      // Only persist when hidden — absent means shown (default on).
      if (m.showOnDashboard === false) entry.show_on_dashboard = false;
      // Only persist when waived — absent means mandatory (default on), so
      // stored configs stay byte-identical until ops turns one off.
      if (m.mandatoryForSession === false) entry.mandatory_for_session = false;
      // Only persist the non-default — absent means 'full'.
      if (m.resultsVariant === "reduced") entry.results_variant = "reduced";
      // Card styling → style.background (+ fontColor). Only emit a renderable
      // background (solid ≥1 color, gradient ≥2); absent → default white box.
      const bgColors = m.bgColors.map((c) => c.trim()).filter(Boolean);
      const style: Record<string, any> = {};
      if (m.bgType === "solid" && bgColors.length >= 1) {
        style.background = { type: "solid", colors: [bgColors[0]] };
      } else if (m.bgType === "gradient" && bgColors.length >= 2) {
        style.background = {
          type: "gradient",
          colors: bgColors,
          direction: m.bgDirection || "to bottom",
        };
      }
      if (m.fontColor.trim()) style.fontColor = m.fontColor.trim();
      if (Object.keys(style).length) entry.style = style;
      if (Object.keys(entry).length) out[fid] = entry;
    });
    return out;
  };

  // Flatten the per-managed-event round-robin state into the stored JSON
  // (keyed by managed_event_id → { default_event_type_id, rules }). Rules with no
  // target are dropped; a managed event with no default and no rules is omitted.
  const buildRoundRobinRouting = () => {
    const out: Record<string, any> = {};
    Object.entries(roundRobinByManagedEvent).forEach(([meId, v]) => {
      let priority = 0;
      const rules = (v.rules ?? [])
        .filter((r) => r.event_type_id)
        .map((r) => ({
          priority: ++priority,
          ...(r.form_id ? { form_id: r.form_id } : {}),
          match: r.match,
          conditions: r.conditions
            .filter((c) => c.field && (c.op === "exists" || c.value !== ""))
            .map((c) => ({
              field: c.field,
              op: c.op,
              ...(c.op === "exists"
                ? {}
                : {
                    value:
                      c.op === "in"
                        ? c.value
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean)
                        : c.value,
                  }),
            })),
          event_type_id: r.event_type_id,
        }));
      const def = v.default_event_type_id || null;
      if (!def && rules.length === 0) return;
      out[meId] = { default_event_type_id: def, rules };
    });
    return out;
  };

  // Parse a JSON textarea; throws a readable error if invalid.
  const parseJson = (text: string, label: string) => {
    const t = text.trim();
    if (!t) return undefined;
    try {
      return JSON.parse(t);
    } catch {
      throw new Error(`${label} is not valid JSON`);
    }
  };

  const selectedForms = useMemo(
    () =>
      forms
        .filter((f: any) => formIds.includes(f.id))
        .map((f: any) => ({ id: f.id, name: f.name })),
    [forms, formIds],
  );

  // Fetch each selected form's questions (for the question + answer pickers).
  const formDetailQueries = useQueries({
    queries: formIds.map((id) => ({
      queryKey: ["form", id],
      queryFn: () => getForm(id),
      enabled: !!id,
    })),
  });
  const questionsByForm = useMemo(() => {
    const map: Record<string, RoutingQuestion[]> = {};
    formDetailQueries.forEach((q, i) => {
      if (q.data) map[formIds[i]] = normalizeQuestions(q.data);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDetailQueries.map((q) => q.dataUpdatedAt).join(","), formIds.join(",")]);

  const selectedGroups = (
    Object.keys(propagateGroups) as PropagationGroup[]
  ).filter((g) => propagateGroups[g]);

  const save = useMutation({
    mutationFn: () => {
      // Images: uploaded CDN urls. Sent as an object (possibly empty) so a
      // removed image clears on save.
      const images: Record<string, string | number> = {};
      if (thumbnailUrl.trim()) images.thumbnail = thumbnailUrl.trim();
      if (heroUrl.trim()) images.hero = heroUrl.trim();
      if (heroRadius.trim() !== "" && !Number.isNaN(Number(heroRadius))) {
        images.hero_radius = Number(heroRadius);
      }
      if (
        thumbnailRadius.trim() !== "" &&
        !Number.isNaN(Number(thumbnailRadius))
      ) {
        images.thumbnail_radius = Number(thumbnailRadius);
      }

      // Card presentation → metadata.card (only include set fields).
      const tags = tagsText
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const card: Record<string, any> = {};
      if (cardTitle.trim()) card.title = cardTitle.trim();
      if (cardDescription.trim()) card.description = cardDescription.trim();
      if (tags.length) card.tags = tags;
      if (variant) card.variant = variant;
      if (accentColor.trim()) card.accent_color = accentColor.trim();

      const baseMetadata = parseJson(metadataText, "Metadata") ?? {};
      // Fold the dedicated subtitle + card fields into metadata.
      // Assessment WhatsApp templates → metadata.assessment_notifications. If ANY
      // is set we're in custom mode: write all three, a blank field → null (skip
      // that message, e.g. a product that folds closure into its report message).
      // All blank → omit the block → the built-in ADHD defaults apply per event.
      const notifSet =
        notifExploration.trim() || notifReport.trim() || notifClosure.trim();
      // A blank template → null (skip). A template with a custom slug variable →
      // { template, slug_param }; otherwise just the template string.
      const notifEvt = (tpl: string, slug: string) => {
        const t = tpl.trim();
        if (!t) return null;
        return slug.trim() ? { template: t, slug_param: slug.trim() } : t;
      };
      const assessment_notifications = notifSet
        ? {
            exploration_outcome: notifEvt(notifExploration, notifExplorationSlug),
            diagnostic_report: notifEvt(notifReport, notifReportSlug),
            journey_closure: notifClosure.trim() || null,
          }
        : null;

      const metadata = {
        ...baseMetadata,
        ...(subtitle.trim() ? { subtitle: subtitle.trim() } : {}),
        ...(assessment_notifications ? { assessment_notifications } : {}),
        ...(Object.keys(card).length ? { card } : {}),
        ...(assessmentPages.results_page
          ? { results_page: assessmentPages.results_page }
          : {}),
        ...(assessmentPages.report_page
          ? { report_page: assessmentPages.report_page }
          : {}),
        ...(onboardingSatisfiedBy.length
          ? { onboarding_satisfied_by_product_ids: onboardingSatisfiedBy }
          : {}),
        ...(carryProfessionalFrom
          ? { carry_professional_from_product_id: carryProfessionalFrom }
          : {}),
        ...(assessmentJourney ? { assessment_journey: true } : {}),
        ...(formResultsPage ? { form_results_page: true } : {}),
        ...(hideBookingPackSummary
          ? { hide_booking_pack_summary: true }
          : {}),
        ...(assessmentCard ? { assessment_card: assessmentCard } : {}),
        ...(onboardingIntro ? { onboarding_intro: onboardingIntro } : {}),
        ...(overviewPage ? { overview_page: overviewPage } : {}),
        ...(pricingCopy ? { pricing_copy: pricingCopy } : {}),
        ...(priority.trim() && Number.isFinite(Number(priority))
          ? { priority: Number(priority) }
          : {}),
        visible,
      };
      const payload: ProductRegionConfigInput = {
        product_id: productId!,
        region_id: regionId!,
        onboarding_form_ids: formIds,
        onboarding_default_bundle_id: defaultBundleId || null,
        onboarding_routing: buildRouting(),
        onboarding_form_meta: buildFormMeta(),
        round_robin_routing: buildRoundRobinRouting(),
        images,
        metadata,
        // Only the ticked presentation groups, and only when other regions exist.
        ...(canPropagate && selectedGroups.length
          ? { propagate_groups: selectedGroups }
          : {}),
      };
      return config
        ? updateProductRegionConfig(config.id, payload)
        : createProductRegionConfig(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["product-region-config"] });
      const mirrored = canPropagate && selectedGroups.length;
      toast({
        title: "Onboarding config saved",
        ...(mirrored
          ? {
              description: `${selectedGroups.length} group${
                selectedGroups.length === 1 ? "" : "s"
              } also applied to ${siblingLabel}`,
            }
          : {}),
      });
      navigate("..");
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  const regionLabel = region === ALL_REGIONS_CODE ? "" : region.toUpperCase();

  return (
    <HasAccess permission="write">
      <div className="max-w-5xl mx-auto p-6 space-y-6 overflow-x-hidden">
        {/* Header */}
        <div className="bg-gray-100 rounded-lg p-4 flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold truncate">
              Configure onboarding{product ? ` — ${product.name}` : ""}
            </h1>
            {regionLabel && (
              <span className="text-xs text-muted-foreground">
                Region {regionLabel}
              </span>
            )}
          </div>
        </div>

        {/* Main (2/3) + sidebar (1/3) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* ---------- Main column ---------- */}
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {productId && (
              <SectionCard
                title="Session short names"
                description="One name per session type, for every region — copy tags point at it, e.g. {{RESCHEDULE_PRICE:joint}}. Saved on the product, separately from the rest of this page."
              >
                <SessionNamesField productId={productId} readOnly={!canWrite} />
              </SectionCard>
            )}
            <SectionCard title="Onboarding & routing">
              <div>
                <p className="text-xs text-gray-500 mb-1">Onboarding forms</p>
                <MultiSelect
                  options={forms.map((f: any) => ({
                    label: f.name,
                    value: f.id,
                  }))}
                  value={formIds}
                  onChange={setFormIds}
                  placeholder="Select onboarding forms"
                  className="h-auto min-h-10 max-h-40 overflow-y-auto items-start py-2"
                />
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Default onboarding bundle
                </p>
                <Select
                  value={defaultBundleId || "NONE"}
                  onValueChange={(v) =>
                    setDefaultBundleId(v === "NONE" ? "" : v)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select default bundle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">No default</SelectItem>
                    {onboardingBundles.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {onboardingBundles.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No onboarding bundles found for this product in{" "}
                    {regionLabel}. Create one first.
                  </p>
                )}
              </div>

              {/* Per-form sub-editor: presentation (title / sub-line / image /
                  slug) + that form's own routing. Shown once ≥1 form is picked.
                  For a single form the picker is hidden in the app, but the
                  presentation still applies. */}
              {formIds.length === 0 ? (
                <p className="text-xs text-gray-400">
                  Select one or more onboarding forms above to configure each
                  form's card + routing.
                </p>
              ) : (
                <div className="space-y-4">
                  {formIds.map((fid) => {
                    const fname =
                      selectedForms.find(
                        (f: { id: string; name: string }) => f.id === fid,
                      )?.name ?? "Form";
                    const m = formMeta[fid] ?? EMPTY_FORM_META;
                    const patchMeta = (patch: Partial<FormMetaEntry>) =>
                      setFormMeta((prev) => ({
                        ...prev,
                        [fid]: { ...m, ...patch },
                      }));
                    return (
                      <div
                        key={fid}
                        className="rounded-lg border border-gray-200 p-4 space-y-3 bg-gray-50/50"
                      >
                        {/* Title on its own row, toggles beneath. Side-by-side
                            worked with two switches but not three — the form
                            name was squeezed to `shrink` and wrapped onto three
                            lines. Stacking also lets the toggle row wrap on
                            narrow viewports instead of overflowing. */}
                        <div className="space-y-2">
                          <p className="text-sm font-semibold text-gray-900">
                            {fname}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                            <label
                              className="flex items-center gap-2"
                              title="Form-results page without the score/tier meter (e.g. the child ADHD form, which is scored internally but never surfaced)."
                            >
                              <span className="text-[11px] text-gray-500">
                                Reduced results (no score)
                              </span>
                              <Switch
                                checked={m.resultsVariant === "reduced"}
                                onCheckedChange={(v) =>
                                  patchMeta({
                                    resultsVariant: v ? "reduced" : "full",
                                  })
                                }
                              />
                            </label>
                            <label
                              className="flex items-center gap-2"
                              title="Patients with an upcoming session for this product see a nudge on their appointment card until they fill this form. Only applies while the form is also shown on the dashboard — a routing-only form has no page for them to open."
                            >
                              <span className="text-[11px] text-gray-500">
                                Mandatory for session
                              </span>
                              <Switch
                                checked={m.mandatoryForSession}
                                onCheckedChange={(v) =>
                                  patchMeta({ mandatoryForSession: v })
                                }
                              />
                            </label>
                            <label className="flex items-center gap-2">
                              <span className="text-[11px] text-gray-500">
                                Show on dashboard
                              </span>
                              <Switch
                                checked={m.showOnDashboard}
                                onCheckedChange={(v) =>
                                  patchMeta({ showOnDashboard: v })
                                }
                              />
                            </label>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-[1fr_128px] gap-3">
                          <div className="space-y-2 min-w-0">
                            <div>
                              <p className="text-[11px] text-gray-500 mb-1">
                                Card title{" "}
                                <span className="text-gray-400">
                                  (overrides the form name)
                                </span>
                              </p>
                              <Input
                                className="h-8 text-sm"
                                placeholder={fname}
                                value={m.title}
                                onChange={(e) =>
                                  patchMeta({ title: e.target.value })
                                }
                              />
                            </div>
                            <div>
                              <p className="text-[11px] text-gray-500 mb-1">
                                Sub-line{" "}
                                <span className="text-gray-400">(optional)</span>
                              </p>
                              <Input
                                className="h-8 text-sm"
                                placeholder="Short line shown under the title"
                                value={m.subtitle}
                                onChange={(e) =>
                                  patchMeta({ subtitle: e.target.value })
                                }
                              />
                            </div>
                            <div>
                              <p className="text-[11px] text-gray-500 mb-1">
                                Slug{" "}
                                <span className="text-gray-400">
                                  (deep link: /onboard?slug=)
                                </span>
                              </p>
                              <Input
                                className="h-8 text-sm"
                                placeholder="e.g. pcos"
                                value={m.slug}
                                onChange={(e) =>
                                  patchMeta({ slug: e.target.value })
                                }
                                onBlur={(e) =>
                                  patchMeta({ slug: slugify(e.target.value) })
                                }
                              />
                              {formIds.some(
                                (other) =>
                                  other !== fid &&
                                  (formMeta[other]?.slug ?? "").trim() &&
                                  slugify(formMeta[other]!.slug) ===
                                    slugify(m.slug) &&
                                  m.slug.trim(),
                              ) && (
                                <p className="text-[11px] text-red-500 mt-1">
                                  Slug must be unique across this product's forms.
                                </p>
                              )}
                            </div>
                          </div>
                          <ImageUploadField
                            label="Card image"
                            value={m.image}
                            onChange={(url) => patchMeta({ image: url })}
                            disabled={!productId}
                            uploader={(f) =>
                              uploadProductRegionConfigImage(
                                f,
                                productId!,
                                "onboarding_form",
                              )
                            }
                          />
                        </div>

                        <div className="border-t border-gray-100 pt-3 space-y-3">
                          <p className="text-xs text-gray-500">
                            Card style{" "}
                            <span className="text-gray-400">
                              (dashboard picker box — default is a white card)
                            </span>
                          </p>
                          <div className="flex flex-wrap items-end gap-3">
                            <div>
                              <p className="text-[11px] text-gray-500 mb-1">
                                Background
                              </p>
                              <Select
                                value={m.bgType || "DEFAULT"}
                                onValueChange={(v) =>
                                  patchMeta(
                                    v === "gradient"
                                      ? {
                                          bgType: "gradient",
                                          bgColors:
                                            m.bgColors.length >= 2
                                              ? m.bgColors
                                              : [
                                                  m.bgColors[0] ?? "#324d38",
                                                  "#1a2e1a",
                                                ],
                                        }
                                      : v === "solid"
                                        ? {
                                            bgType: "solid",
                                            bgColors: m.bgColors.length
                                              ? [m.bgColors[0]]
                                              : ["#324d38"],
                                          }
                                        : { bgType: "" },
                                  )
                                }
                              >
                                <SelectTrigger className="h-8 text-sm w-40">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="DEFAULT">
                                    Default (white)
                                  </SelectItem>
                                  <SelectItem value="solid">Solid</SelectItem>
                                  <SelectItem value="gradient">
                                    Gradient
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {m.bgType === "gradient" && (
                              <div>
                                <p className="text-[11px] text-gray-500 mb-1">
                                  Direction
                                </p>
                                <Select
                                  value={m.bgDirection || "to bottom"}
                                  onValueChange={(v) =>
                                    patchMeta({ bgDirection: v })
                                  }
                                >
                                  <SelectTrigger className="h-8 text-sm w-44">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {GRADIENT_DIRECTIONS.map((d) => (
                                      <SelectItem key={d.value} value={d.value}>
                                        {d.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>

                          {m.bgType === "solid" && (
                            <div>
                              <p className="text-[11px] text-gray-500 mb-1">
                                Background color
                              </p>
                              <ColorInput
                                value={m.bgColors[0] ?? ""}
                                onChange={(v) => patchMeta({ bgColors: [v] })}
                                ariaLabel="Background color"
                              />
                            </div>
                          )}

                          {m.bgType === "gradient" && (
                            <div className="space-y-2">
                              <p className="text-[11px] text-gray-500">
                                Gradient colors{" "}
                                <span className="text-gray-400">
                                  (in order; min 2)
                                </span>
                              </p>
                              {m.bgColors.map((c, i) => (
                                <div key={i} className="flex items-center gap-2">
                                  <ColorInput
                                    value={c}
                                    onChange={(v) =>
                                      patchMeta({
                                        bgColors: m.bgColors.map((x, j) =>
                                          j === i ? v : x,
                                        ),
                                      })
                                    }
                                    ariaLabel={`Gradient color ${i + 1}`}
                                  />
                                  {m.bgColors.length > 2 && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() =>
                                        patchMeta({
                                          bgColors: m.bgColors.filter(
                                            (_, j) => j !== i,
                                          ),
                                        })
                                      }
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  patchMeta({
                                    bgColors: [...m.bgColors, "#ffffff"],
                                  })
                                }
                              >
                                <Plus className="h-3 w-3 mr-1" /> Add color
                              </Button>
                              <div
                                className="h-10 rounded-md border border-gray-200"
                                style={{
                                  background: `linear-gradient(${
                                    m.bgDirection || "to bottom"
                                  }, ${m.bgColors.join(", ")})`,
                                }}
                              />
                            </div>
                          )}

                          <div>
                            <p className="text-[11px] text-gray-500 mb-1">
                              Font color{" "}
                              <span className="text-gray-400">
                                (blank → default)
                              </span>
                            </p>
                            <ColorInput
                              value={m.fontColor}
                              onChange={(v) => patchMeta({ fontColor: v })}
                              ariaLabel="Font color"
                            />
                          </div>
                        </div>

                        <div className="border-t border-gray-100 pt-3">
                          <p className="text-xs text-gray-500 mb-2">
                            Routing — answers → bundle
                          </p>
                          <OnboardingRoutingField
                            rules={rulesByForm[fid] ?? []}
                            onChange={(next) =>
                              setRulesByForm((prev) => ({
                                ...prev,
                                [fid]: next,
                              }))
                            }
                            forms={selectedForms}
                            bundles={onboardingBundles}
                            questionsByForm={questionsByForm}
                            lockedFormId={fid}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionCard>

            <SectionCard
              title="Skip onboarding after other products"
              description="If the user owns a bundle from any product ticked here, they skip this product's onboarding and go straight to standard packs (e.g. tick 'ADHD Assessment' on Clinical Therapy)."
            >
              <MultiSelect
                options={(products ?? [])
                  .filter((p) => p.id !== productId)
                  .map((p) => ({ label: p.name, value: p.id }))}
                value={onboardingSatisfiedBy}
                onChange={setOnboardingSatisfiedBy}
                placeholder="Select products…"
                className="h-auto min-h-10 max-h-40 overflow-y-auto items-start py-2"
              />
            </SectionCard>

            <SectionCard
              title="Carry therapist from another product"
              description="When a user buys this product, assign the therapist from their most recent bundle of the product picked here — bypassing round-robin (e.g. pick 'ADHD Assessment' on Clinical Therapy)."
            >
              <Select
                value={carryProfessionalFrom || "NONE"}
                onValueChange={(v) =>
                  setCarryProfessionalFrom(v === "NONE" ? "" : v)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select source product" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">None</SelectItem>
                  {(products ?? [])
                    .filter((p) => p.id !== productId)
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </SectionCard>

            <SectionCard
              title="Round-robin routing (by managed event)"
              description="When a session has no assigned professional, route to a round-robin pool by form answers. First matching rule → else the managed event's default → else the current first-match."
            >
              {roundRobinManagedEvents.length === 0 ? (
                <p className="text-xs text-gray-400">
                  No round-robin-enabled managed events for this product/region.
                </p>
              ) : (
                <div className="space-y-4">
                  {roundRobinManagedEvents.map((m) => {
                    const pools = poolsByManagedEvent[m.id] ?? [];
                    const entry =
                      roundRobinByManagedEvent[m.id] ?? {
                        default_event_type_id: "",
                        rules: [],
                      };
                    const patch = (
                      p: Partial<{
                        default_event_type_id: string;
                        rules: RoundRobinRule[];
                      }>,
                    ) =>
                      setRoundRobinByManagedEvent((prev) => ({
                        ...prev,
                        [m.id]: { ...entry, ...p },
                      }));
                    return (
                      <div
                        key={m.id}
                        className="rounded-md border border-gray-200 p-3 space-y-3"
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">{m.name}</p>
                          <span className="text-[11px] text-gray-400">
                            {m.category}
                          </span>
                        </div>

                        {pools.length === 0 ? (
                          <p className="text-xs text-amber-600">
                            No round-robin pools configured for this managed
                            event — add them in Supply config first.
                          </p>
                        ) : (
                          <>
                            <div>
                              <p className="text-[11px] text-gray-500 mb-1">
                                Default round-robin
                              </p>
                              <Select
                                value={entry.default_event_type_id || "NONE"}
                                onValueChange={(v) =>
                                  patch({
                                    default_event_type_id:
                                      v === "NONE" ? "" : v,
                                  })
                                }
                              >
                                <SelectTrigger className="h-8 text-sm">
                                  <SelectValue placeholder="Select default" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="NONE">
                                    None (use first-match)
                                  </SelectItem>
                                  {pools.map((et) => (
                                    <SelectItem key={et.id} value={et.id}>
                                      {et.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="border-t border-gray-100 pt-3">
                              <p className="text-xs text-gray-500 mb-2">
                                Routing — answers → round-robin
                              </p>
                              <RoundRobinRoutingField
                                rules={entry.rules}
                                onChange={(next) => patch({ rules: next })}
                                forms={selectedForms}
                                eventTypes={pools}
                                questionsByForm={questionsByForm}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionCard>

            <SectionCard
              title="Card presentation"
              description="Shown on the dashboard product card."
            >
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Title override{" "}
                  <span className="text-gray-400">
                    (blank → uses the product name)
                  </span>
                </p>
                <input
                  className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm"
                  placeholder={product?.name ?? "Product name"}
                  value={cardTitle}
                  onChange={(e) => setCardTitle(e.target.value)}
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Description override{" "}
                  <span className="text-gray-400">
                    (blank → uses the product description)
                  </span>
                </p>
                <input
                  className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm"
                  placeholder="Short card description"
                  value={cardDescription}
                  onChange={(e) => setCardDescription(e.target.value)}
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Tags{" "}
                  <span className="text-gray-400">
                    (comma-separated; the only source for card tags)
                  </span>
                </p>
                <input
                  className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm"
                  placeholder="e.g. Individual, Tele-health"
                  value={tagsText}
                  onChange={(e) => setTagsText(e.target.value)}
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Card color{" "}
                  <span className="text-gray-400">(shown on hover)</span>
                </p>
                <CardColorField value={variant} onChange={setVariant} />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Accent color{" "}
                  <span className="text-gray-400">
                    (in-product buttons, gradients & payment page; blank →
                    default indigo)
                  </span>
                </p>
                <ColorInput
                  value={accentColor}
                  onChange={setAccentColor}
                  ariaLabel="Product accent color"
                />
              </div>
              {canPropagate && (
                <PropagateToggle
                  checked={propagateGroups.card}
                  onToggle={() => toggleGroup("card")}
                  regions={siblingLabel}
                />
              )}
            </SectionCard>

            <SectionCard
              title="Onboarding overview steps"
              description="Steps card on the product page (form → sessions → payment). Session rows come from the bundle's session-types titles/descriptions; the payment step follows the bundle's payment timing automatically."
            >
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={!!overviewPage}
                  onCheckedChange={(c: boolean) =>
                    setOverviewPage(c ? (overviewPage ?? {}) : null)
                  }
                />
                <span className="text-sm font-medium">
                  Use the overview steps card
                </span>
              </label>
              {overviewPage && (
                <div className="space-y-3 border-l-2 border-gray-100 pl-4">
                  {[
                    ["form_step", "title", "Form step — title"],
                    ["form_step", "body", "Form step — description"],
                    ["payment_step", "title", "Payment step — title"],
                    ["payment_step", "body", "Payment step — description"],
                  ].map(([section, key, label]) => (
                    <div key={`${section}.${key}`}>
                      <p className="text-xs text-gray-500 mb-1">{label}</p>
                      <input
                        className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm"
                        placeholder="Default copy applies when blank"
                        value={overviewPage[section]?.[key] ?? ""}
                        onChange={(e) =>
                          setOverviewPage({
                            ...overviewPage,
                            [section]: {
                              ...(overviewPage[section] ?? {}),
                              [key]: e.target.value || undefined,
                            },
                          })
                        }
                      />
                    </div>
                  ))}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Button label</p>
                    <input
                      className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm"
                      placeholder="Start onboarding"
                      value={overviewPage.cta ?? ""}
                      onChange={(e) =>
                        setOverviewPage({
                          ...overviewPage,
                          cta: e.target.value || undefined,
                        })
                      }
                    />
                  </div>
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={overviewPage.show_payment_step !== false}
                      onCheckedChange={(c: boolean) =>
                        setOverviewPage({
                          ...overviewPage,
                          show_payment_step: c ? undefined : false,
                        })
                      }
                    />
                    <span className="text-xs text-gray-600">
                      Show the payment step row
                    </span>
                  </label>
                </div>
              )}
            </SectionCard>

            <SectionCard
              title="Onboarding intro page"
              description="Optional screen shown after 'Get started', before the onboarding form. Blank fields use the defaults."
            >
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={!!onboardingIntro}
                  onCheckedChange={(c: boolean) =>
                    setOnboardingIntro(c ? (onboardingIntro ?? {}) : null)
                  }
                />
                <span className="text-sm font-medium">
                  Show an intro screen before the form
                </span>
              </label>
              {onboardingIntro && (
                <div className="space-y-3 border-l-2 border-gray-100 pl-4">
                  {[
                    ["title", "Headline", false],
                    ["subtitle", "Subtitle", true],
                    ["cta", "Button label", false],
                    ["terms_note", "Terms line", false],
                  ].map(([key, label, multiline]) => (
                    <div key={key as string}>
                      <p className="text-xs text-gray-500 mb-1">
                        {label as string}
                      </p>
                      {multiline ? (
                        <textarea
                          className="w-full min-h-[60px] rounded-md border border-gray-200 p-2 text-sm"
                          value={onboardingIntro[key as string] ?? ""}
                          onChange={(e) =>
                            setOnboardingIntro({
                              ...onboardingIntro,
                              [key as string]: e.target.value || undefined,
                            })
                          }
                        />
                      ) : (
                        <input
                          className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm"
                          value={onboardingIntro[key as string] ?? ""}
                          onChange={(e) =>
                            setOnboardingIntro({
                              ...onboardingIntro,
                              [key as string]: e.target.value || undefined,
                            })
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            <SectionCard
              title="Pricing page copy"
              description="Copy on the Choose-a-Bundle page's first-session intro (headline, steps, CTA) and the 'after your first session' section. Blank fields use the app's shipped defaults; per region."
            >
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={!!pricingCopy}
                  onCheckedChange={(c: boolean) =>
                    setPricingCopy(c ? (pricingCopy ?? {}) : null)
                  }
                />
                <span className="text-sm font-medium">
                  Customise the pricing page copy
                </span>
              </label>
              {pricingCopy && (
                <div className="space-y-3 border-l-2 border-gray-100 pl-4">
                  {(
                    [
                      ["headline", "Headline", false],
                      ["subcopy", "Subcopy", true],
                      ["introNote", "Intro note (under the price)", false],
                      ["ctaLabel", "First-session button label", false],
                      ["stepsTitle", "Steps section title", false],
                      ["afterBadge", "After-session badge", false],
                      ["continueTitle", "Continue section title", false],
                      [
                        "continueSubtitle",
                        "Continue subtitle (use {price} for the 'from …/session' amount)",
                        true,
                      ],
                    ] as [string, string, boolean][]
                  ).map(([key, label, multiline]) => (
                    <div key={key}>
                      <p className="text-xs text-gray-500 mb-1">{label}</p>
                      {multiline ? (
                        <textarea
                          className="w-full min-h-[60px] rounded-md border border-gray-200 p-2 text-sm"
                          placeholder="Default copy applies when blank"
                          value={pricingCopy[key] ?? ""}
                          onChange={(e) =>
                            setPricingCopy({
                              ...pricingCopy,
                              [key]: e.target.value || undefined,
                            })
                          }
                        />
                      ) : (
                        <input
                          className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm"
                          placeholder="Default copy applies when blank"
                          value={pricingCopy[key] ?? ""}
                          onChange={(e) =>
                            setPricingCopy({
                              ...pricingCopy,
                              [key]: e.target.value || undefined,
                            })
                          }
                        />
                      )}
                    </div>
                  ))}

                  {/* "What to expect" steps (blank list → default steps). */}
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">
                      Steps (title + description; blank list → default steps)
                    </p>
                    {(
                      (pricingCopy.steps ?? []) as {
                        title?: string;
                        description?: string;
                      }[]
                    ).map((step, i) => {
                      const steps = (pricingCopy.steps ?? []) as {
                        title?: string;
                        description?: string;
                      }[];
                      const patch = (p: Record<string, string | undefined>) =>
                        setPricingCopy({
                          ...pricingCopy,
                          steps: steps.map((s, j) =>
                            j === i ? { ...s, ...p } : s,
                          ),
                        });
                      return (
                        <div key={i} className="flex gap-2 items-start">
                          <div className="flex-1 space-y-1">
                            <input
                              className="w-full h-8 rounded-md border border-gray-200 px-2 text-sm"
                              placeholder="Title"
                              value={step.title ?? ""}
                              onChange={(e) =>
                                patch({ title: e.target.value || undefined })
                              }
                            />
                            <textarea
                              className="w-full min-h-[48px] rounded-md border border-gray-200 p-2 text-sm"
                              placeholder="Description"
                              value={step.description ?? ""}
                              onChange={(e) =>
                                patch({
                                  description: e.target.value || undefined,
                                })
                              }
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              setPricingCopy({
                                ...pricingCopy,
                                steps: steps.filter((_, j) => j !== i),
                              })
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setPricingCopy({
                          ...pricingCopy,
                          steps: [
                            ...((pricingCopy.steps ?? []) as unknown[]),
                            {},
                          ],
                        })
                      }
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add step
                    </Button>
                  </div>
                </div>
              )}
              {canPropagate && (
                <PropagateToggle
                  checked={propagateGroups.pricing_copy}
                  onToggle={() => toggleGroup("pricing_copy")}
                  regions={siblingLabel}
                  note="Check the copy for currency or price mentions first — it is not translated or converted."
                />
              )}
            </SectionCard>

            <SectionCard
              title="Assessments & Tests card"
              description="Opt this product into the dashboard's Assessments & Tests section. The two journey lines come from the user's usage state; copy is overridable below."
            >
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={!!assessmentCard}
                  onCheckedChange={(c: boolean) =>
                    setAssessmentCard(c ? (assessmentCard ?? {}) : null)
                  }
                />
                <span className="text-sm font-medium">
                  Show in Assessments &amp; Tests
                </span>
              </label>
              {assessmentCard && (
                <div className="space-y-4 border-l-2 border-gray-100 pl-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      Card title{" "}
                      <span className="text-gray-400">
                        (blank → product name)
                      </span>
                    </p>
                    <input
                      className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm"
                      value={assessmentCard.title ?? ""}
                      onChange={(e) =>
                        setAssessmentCard({
                          ...assessmentCard,
                          title: e.target.value || undefined,
                        })
                      }
                    />
                  </div>
                  <ImageUploadField
                    label="Card image (square)"
                    value={assessmentCard.image ?? ""}
                    onChange={(url) =>
                      setAssessmentCard({ ...assessmentCard, image: url })
                    }
                    disabled={!productId}
                    uploader={(f) =>
                      uploadProductRegionConfigImage(
                        f,
                        productId!,
                        "assessment-card",
                      )
                    }
                  />
                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      Description{" "}
                      <span className="text-gray-400">
                        (shown before the user starts; blank → product
                        description)
                      </span>
                    </p>
                    <textarea
                      className="w-full min-h-[60px] rounded-md border border-gray-200 p-2 text-sm"
                      value={assessmentCard.description ?? ""}
                      onChange={(e) =>
                        setAssessmentCard({
                          ...assessmentCard,
                          description: e.target.value || undefined,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700">
                      Journey-state lines (blank → defaults; line 2 supports{" "}
                      {"{count}"} and {"{total}"})
                    </p>
                    {[
                      ["IN_PROGRESS", "Assessment in progress"],
                      ["COMPLETE_PENDING_RESULTS", "Complete, results pending"],
                      ["COMPLETE_RESULTS", "Complete, results recorded"],
                      ["DIVA_IN_PROGRESS", "Diagnostic in progress"],
                      ["DIVA_PENDING_REPORT", "Diagnostic done, report pending"],
                      ["DIVA_REPORT_READY", "Report ready"],
                    ].map(([key, label]) => (
                      <div key={key} className="grid grid-cols-2 gap-2">
                        <input
                          className="h-8 rounded-md border border-gray-200 px-2 text-xs"
                          placeholder={`${label} — line 1`}
                          value={assessmentCard.states?.[key]?.line1 ?? ""}
                          onChange={(e) =>
                            setAssessmentCard({
                              ...assessmentCard,
                              states: {
                                ...(assessmentCard.states ?? {}),
                                [key]: {
                                  ...(assessmentCard.states?.[key] ?? {}),
                                  line1: e.target.value || undefined,
                                },
                              },
                            })
                          }
                        />
                        <input
                          className="h-8 rounded-md border border-gray-200 px-2 text-xs"
                          placeholder={`${label} — line 2`}
                          value={assessmentCard.states?.[key]?.line2 ?? ""}
                          onChange={(e) =>
                            setAssessmentCard({
                              ...assessmentCard,
                              states: {
                                ...(assessmentCard.states ?? {}),
                                [key]: {
                                  ...(assessmentCard.states?.[key] ?? {}),
                                  line2: e.target.value || undefined,
                                },
                              },
                            })
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </SectionCard>

            <SectionCard
              title="Assessment journey pages"
              description="Results + report pages shown after the assessment completes. Copy defaults apply when fields are blank; prices always come live from the selected bundle/product."
            >
              <div className="flex items-center justify-between rounded-md border border-gray-200 p-3">
                <div>
                  <p className="text-sm font-medium">
                    Assessment journey completion CTAs
                  </p>
                  <p className="text-xs text-gray-500">
                    When a pack finishes, show the results / report buttons on
                    the product page. Leave off for normal products (they keep
                    the standard scheduling flow).
                  </p>
                </div>
                <Switch
                  checked={assessmentJourney}
                  onCheckedChange={setAssessmentJourney}
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-gray-200 p-3">
                <div>
                  <p className="text-sm font-medium">
                    Pre-payment form-results page (ADHD)
                  </p>
                  <p className="text-xs text-gray-500">
                    After the form + OTP, show the High / Moderate / Low results
                    screen before payment. Off (default) keeps the old flow,
                    where the result is shown on the form&apos;s own final
                    screen. The pay-first vs book-first step after it is
                    unaffected (still driven by the bundle&apos;s payment timing).
                  </p>
                </div>
                <Switch
                  checked={formResultsPage}
                  onCheckedChange={setFormResultsPage}
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-gray-200 p-3">
                <div>
                  <p className="text-sm font-medium">
                    Hide pack summary on booking confirmation
                  </p>
                  <p className="text-xs text-gray-500">
                    On the booking-confirm page, hide the inner pack card (bundle
                    name + &quot;not charged extra&quot; + sessions-remaining).
                    The session&apos;s name, date, professional, and time stay.
                  </p>
                </div>
                <Switch
                  checked={hideBookingPackSummary}
                  onCheckedChange={setHideBookingPackSummary}
                />
              </div>
              <div className="rounded-lg border p-4 space-y-3">
                <p className="text-sm font-medium">
                  Assessment WhatsApp templates
                </p>
                <p className="text-xs text-muted-foreground">
                  WATI template names fired to the patient. Leave all blank →
                  the default ADHD templates apply. If you set any, a blank field
                  means &quot;don&apos;t send that message&quot; (e.g. a product
                  that folds closure into its report message).
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      Exploration outcome template
                    </p>
                    <Input
                      className="h-9"
                      placeholder="adhd_exploration_outcome_"
                      value={notifExploration}
                      onChange={(e) => setNotifExploration(e.target.value)}
                    />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      Slug variable{" "}
                      <span className="text-gray-400">(blank → results_slug)</span>
                    </p>
                    <Input
                      className="h-9"
                      placeholder="results_slug"
                      value={notifExplorationSlug}
                      onChange={(e) => setNotifExplorationSlug(e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      Diagnostic report template
                    </p>
                    <Input
                      className="h-9"
                      placeholder="adhd_diagnostic_report"
                      value={notifReport}
                      onChange={(e) => setNotifReport(e.target.value)}
                    />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      Slug variable{" "}
                      <span className="text-gray-400">(blank → report_slug)</span>
                    </p>
                    <Input
                      className="h-9"
                      placeholder="report_slug"
                      value={notifReportSlug}
                      onChange={(e) => setNotifReportSlug(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    Journey closure (cron, after debrief) — blank to skip
                  </p>
                  <Input
                    className="h-9"
                    placeholder="adhd_journey_closure"
                    value={notifClosure}
                    onChange={(e) => setNotifClosure(e.target.value)}
                  />
                </div>
              </div>
              <AssessmentPagesField
                value={assessmentPages}
                onChange={setAssessmentPages}
                bundles={productBundles}
                products={(products ?? []).map((p: any) => ({
                  id: p.id,
                  name: p.name,
                }))}
                forms={selectedForms}
              />
            </SectionCard>

            <SectionCard
              title="Advanced"
              description="Extra per-region metadata not covered by the fields above."
            >
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Metadata <span className="text-gray-400">(JSON)</span>
                </p>
                <textarea
                  className="w-full min-h-[90px] rounded-md border border-gray-200 p-2 font-mono text-xs"
                  placeholder={`{ "key": "value" }`}
                  value={metadataText}
                  onChange={(e) => setMetadataText(e.target.value)}
                />
              </div>
            </SectionCard>
          </div>

          {/* ---------- Sidebar ---------- */}
          <div className="lg:col-span-1 space-y-6 min-w-0">
            <SectionCard title="Visibility">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-0.5">
                  <p className="text-sm">Show on dashboard</p>
                  <p className="text-xs text-gray-400">
                    When off, the product card is hidden in this region even if
                    it has active bundles.
                  </p>
                </div>
                <Switch checked={visible} onCheckedChange={setVisible} />
              </div>
              <div className="border-t border-gray-100 pt-3">
                <p className="text-sm mb-1">Display priority</p>
                <Input
                  type="number"
                  className="h-8 text-sm w-28"
                  placeholder="—"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Lower shows first on the dashboard. Leave blank to sort after
                  prioritized products (alphabetical).
                </p>
              </div>
            </SectionCard>

            <SectionCard title="Product images">
              <ImageUploadField
                label="Thumbnail (card)"
                value={thumbnailUrl}
                onChange={setThumbnailUrl}
                disabled={!productId}
                uploader={(f) =>
                  uploadProductRegionConfigImage(f, productId!, "thumbnail")
                }
              />
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Thumbnail corner radius{" "}
                  <span className="text-gray-400">(px, blank → default)</span>
                </p>
                <input
                  type="number"
                  min={0}
                  className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm"
                  placeholder="e.g. 12"
                  value={thumbnailRadius}
                  onChange={(e) => setThumbnailRadius(e.target.value)}
                />
              </div>
              <ImageUploadField
                label="Hero (details page)"
                value={heroUrl}
                onChange={setHeroUrl}
                disabled={!productId}
                uploader={(f) =>
                  uploadProductRegionConfigImage(f, productId!, "hero")
                }
              />
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Hero corner radius{" "}
                  <span className="text-gray-400">(px, blank → default)</span>
                </p>
                <input
                  type="number"
                  min={0}
                  className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm"
                  placeholder="e.g. 16"
                  value={heroRadius}
                  onChange={(e) => setHeroRadius(e.target.value)}
                />
              </div>
              {canPropagate && (
                <PropagateToggle
                  checked={propagateGroups.images}
                  onToggle={() => toggleGroup("images")}
                  regions={siblingLabel}
                  note="Images are uploaded per product, so the same files are valid in every region."
                />
              )}
            </SectionCard>

            <SectionCard title="Page details">
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Subtitle{" "}
                  <span className="text-gray-400">
                    (below the title on the product page)
                  </span>
                </p>
                <input
                  className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm"
                  placeholder="e.g. Pick the plan that fits you"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                />
              </div>
              {canPropagate && (
                <PropagateToggle
                  checked={propagateGroups.page_details}
                  onToggle={() => toggleGroup("page_details")}
                  regions={siblingLabel}
                />
              )}
            </SectionCard>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate("..")}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || isLoading || !regionId}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </HasAccess>
  );
}
